import { SearchResultItem } from "../types/geocode-ui.types";

/**
 * Extract country code from geocoding result with fallbacks
 * @param searchResultItem - The geocoding result from backend
 * @param defaultCountryCode - Default country code if none found (default: 'FR')
 * @returns ISO country code (2 letters)
 */
export function extractCountryCode(
    searchResultItem: SearchResultItem | null | undefined,
    defaultCountryCode: string = "CH",
): string {
    if ( searchResultItem == null ) {
        return defaultCountryCode;
    }

    // Extract country code from location.country (ISO code)
    if ( searchResultItem.location?.country != null && searchResultItem.location.country.length === 2 ) {
        return searchResultItem.location.country.toUpperCase();
    }

    return defaultCountryCode;
}

/**
 * Extract currency from country code
 * @param countryCode - ISO country code
 * @returns Currency code (3 letters)
 */
export function extractCurrencyFromCountry( countryCode: string ): string {
    const countryCurrencyMappings: Record<string, string> = {
        "FR": "EUR", // France
        "DE": "EUR", // Germany
        "ES": "EUR", // Spain
        "IT": "EUR", // Italy
        "BE": "EUR", // Belgium
        "NL": "EUR", // Netherlands
        "PT": "EUR", // Portugal
        "GB": "GBP", // United Kingdom
        "CH": "CHF", // Switzerland
        "PL": "PLN", // Poland
        "CZ": "CZK", // Czech Republic
        "HU": "HUF", // Hungary
        "DK": "DKK", // Denmark
        "SE": "SEK", // Sweden
        "NO": "NOK", // Norway
    };

    return countryCurrencyMappings[countryCode.toUpperCase()] ?? "EUR";
}

/**
 * Build complete itinerary country parameters from geocoding results
 * @param fromLocation - Departure location from geocoding
 * @param toLocation - Arrival location from geocoding
 * @returns Object with currency, fromCountry, toCountry
 */
export function buildItineraryCountryParams(
    fromLocation: SearchResultItem | null | undefined,
    toLocation: SearchResultItem | null | undefined,
) {
    const fromCountry = extractCountryCode( fromLocation, "FR" );
    const toCountry = extractCountryCode( toLocation, "FR" );

    // Use departure country's currency as default, or EUR if same country
    let currency = extractCurrencyFromCountry( fromCountry );

    // If cross-border itinerary within Eurozone, prefer EUR
    const eurozoneCountries = [ "FR", "DE", "ES", "IT", "BE", "NL", "PT", "AT", "FI", "IE", "LU" ];
    if ( fromCountry !== toCountry &&
      eurozoneCountries.includes( fromCountry ) &&
      eurozoneCountries.includes( toCountry ) ) {
        currency = "EUR";
    }

    return {
        currency,
        fromCountry,
        toCountry,
    };
}
